"use client"

import { useState, useEffect, useContext } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { AuthContext } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import "./GroupDetails.css"
import { API_URL } from "../config"

const GroupDetails = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user } = useContext(AuthContext)
    const { t } = useLanguage()

    const [group, setGroup] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        fetchGroupDetails()
    }, [id])

    const fetchGroupDetails = async () => {
        try {
            setLoading(true)
            const token = localStorage.getItem("token")

            const response = await fetch(`${API_URL}/api/groups/${id}`, {
                headers: { "x-auth-token": token },
            })

            if (response.ok) {
                const data = await response.json()
                setGroup(data)
            } else {
                setError("Failed to fetch Station details")
            }
        } catch (error) {
            console.error("Error fetching Station details:", error)
            setError("Error loading Station details")
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        try {
            setDeleting(true)
            const response = await fetch(`/api/groups/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            })

            if (response.ok) {
                navigate("/group-management")
            } else {
                setError("Failed to delete Station")
            }
        } catch (error) {
            console.error("Error deleting Station:", error)
            setError("Error deleting Station")
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    if (loading) {
        return (
            <div className="group-details-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading Station details...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="group-details-container">
                <div className="alert alert-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    {error}
                </div>
                <Link to="/group-management" className="btn btn-secondary">
                    <i className="fas fa-arrow-left"></i>
                    Back to Stations
                </Link>
            </div>
        )
    }

    if (!group) {
        return (
            <div className="group-details-container">
                <div className="alert alert-warning">
                    <i className="fas fa-info-circle"></i>
                    Station not found
                </div>
                <Link to="/group-management" className="btn btn-secondary">
                    <i className="fas fa-arrow-left"></i>
                    Back to Stations
                </Link>
            </div>
        )
    }

    return (
        <div className="group-details-container">
            {/* Header */}
            <div className="group-details-header">
                <div className="header-content">
                    <div className="breadcrumb">
                        <Link to="/dashboard">Dashboard</Link>
                        <span>/</span>
                        <Link to="/group-management">Stations</Link>
                        <span>/</span>
                        <span>{group.name}</span>
                    </div>
                    <h1 className="page-title">
                        <i className="fas fa-layer-group"></i> <label>Station Name:</label>
                        {group.name}
                    </h1>
                    <p className="group-description">{group.description}</p>
                </div>
                <div className="header-actions">
                    <Link to={`/edit-group/${group._id}`} className="btn btn-primary">
                        <i className="fas fa-edit"></i>
                        Edit Station
                    </Link>
                    <button onClick={() => setShowDeleteModal(true)} className="btn btn-danger">
                        <i className="fas fa-trash"></i>
                        Delete
                    </button>
                </div>
            </div>

            {/* Group Info Cards */}
            <div className="group-info-grid">
                <div className="info-card">
                    <div className="info-header">
                        <i className="fas fa-info-circle"></i>
                        <h3>Station Information</h3>
                    </div>
                    <div className="info-content">
                        <div className="info-item">
                            <label>Station ID:</label>
                            <span>{group.groupId}</span>
                        </div>
                        <div className="info-item">
                            <label>Created:</label>
                            <span>{formatDate(group.createdAt)}</span>
                        </div>
                        <div className="info-item">
                            <label>Last Updated:</label>
                            <span>{formatDate(group.updatedAt)}</span>
                        </div>
                        <div className="info-item">
                            <label>Created by:</label>
                            <span>{group.user.name || "Unknown"}</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
    <div className="info-header">
        <i className="fas fa-chart-bar"></i>
        <h3>Statistics</h3>
    </div>
    <div className="info-content">
        <div className="stat-item">
            <div className="stat-number">
                {group.selectedDevices?.reduce((acc, device) => acc + (device.selectedParameters?.length || 0), 0) || 0}
            </div>
            <div className="stat-label">Device Parameters</div>
        </div>
        <div className="stat-item">
            <div className="stat-number">{group.apiParams?.length || 0}</div>
            <div className="stat-label">Forecasting Parameters</div>
        </div>
        <div className="stat-item">
            <div className="stat-number">
                {(group.selectedDevices?.reduce((acc, device) => acc + (device.selectedParameters?.length || 0), 0) || 0) + (group.apiParams?.length || 0)}
            </div>
            <div className="stat-label">Total Parameters</div>
        </div>
    </div>
</div>

            </div>

            {/* Parameters Section */}
            <div className="parameters-section">
                {/* Device Parameters */}
                <div className="parameters-card">
                    <div className="card-header">
                        <h3>
                            <i className="fas fa-microchip"></i>
                            Device Parameters ({group.selectedDevices?.reduce((acc, device) => acc + (device.selectedParameters?.length || 0), 0)})
                        </h3>
                    </div>
                    <div className="card-body">
                        {group.selectedDevices && group.selectedDevices.length > 0 ? (
                            <div className="parameters-grid">
                                {group.selectedDevices.map((device, deviceIndex) => (
                                    <div key={device._id || deviceIndex} className="device-section">
                                        <h4>Device: {device.name}</h4>
                                        {device.selectedParameters && device.selectedParameters.length > 0 ? (
                                            device.selectedParameters.map((param, index) => (
                                                <div key={index} className="parameter-item">
                                                    <div className="parameter-icon">
                                                        <i className={getParameterIcon(param)}></i>
                                                    </div>
                                                    <div className="parameter-info">
                                                        <span className="parameter-name">{param}</span>
                                                        <span className="parameter-type">Device</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-state">
                                                <i className="fas fa-inbox"></i>
                                                <p>No parameters for this device</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <i className="fas fa-inbox"></i>
                                <p>No device parameters configured</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* API Parameters */}
                <div className="parameters-card">
                    <div className="card-header">
                        <h3>
                            <i className="fas fa-cloud"></i>
                            Forecasting Parameters ({group.apiParams?.length || 0})
                        </h3>
                    </div>
                    <div className="card-body">
                        {group.apiParams && group.apiParams.length > 0 ? (
                            <div className="parameters-grid">
                                {group.apiParams.map((param, index) => (
                                    <div key={index} className="parameter-item">
                                        <div className="parameter-icon">
                                            <i className={getParameterIcon(param)}></i>
                                        </div>
                                        <div className="parameter-info">
                                            <span className="parameter-name">{param}</span>
                                            <span className="parameter-type">API</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <i className="fas fa-inbox"></i>
                                <p>No API parameters configured</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>


            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button onClick={() => setShowDeleteModal(false)} className="modal-close">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="warning-icon">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <p>
                                Are you sure you want to delete the Station <strong>"{group.name}"</strong>?
                            </p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button onClick={() => setShowDeleteModal(false)} className="btn btn-secondary" disabled={deleting}>
                                Cancel
                            </button>
                            <button onClick={handleDelete} className="btn btn-danger" disabled={deleting}>
                                {deleting ? (
                                    <>
                                        <div className="spinner-small"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-trash"></i>
                                        Delete Station
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// Helper function to get parameter icons
const getParameterIcon = (param) => {
    const paramLower = param.toLowerCase()
    if (paramLower.includes("temp")) return "fas fa-thermometer-half"
    if (paramLower.includes("humid")) return "fas fa-tint"
    if (paramLower.includes("wind")) return "fas fa-wind"
    if (paramLower.includes("pressure")) return "fas fa-gauge"
    if (paramLower.includes("rain") || paramLower.includes("precip")) return "fas fa-cloud-rain"
    if (paramLower.includes("solar") || paramLower.includes("radiation")) return "fas fa-sun"
    if (paramLower.includes("dew")) return "fas fa-droplet"
    return "fas fa-chart-line"
}

export default GroupDetails
